import React from 'react';
import useSetAccessor from '@components/CaseDetails/CaseInUse/hooks/useGetCaseAsAccessor/useSetAccessor/useSetAccessor';
import { UserAccessEnum } from '@interfaces/SurgeryRequest/UserAccessEnum';
import { SurgeryRequestAccessor } from '@interfaces/SurgeryRequest/SurgeryRequestAccessor';
import { AxiosError, AxiosResponse } from 'axios';
import { BlockReleaseRequestAccessor } from '@components/BlockRelease/BlockReleaseRequest/BlockReleaseRequestContext/BlockReleaseRequestContext.types';
import { useModalContext } from '@components/Modal/ModalContextProvider';
import { BLOCK_RELEASE_IN_USE_MODAL } from '@components/Modal/modalConstants';
import { UserProfileDetailsSelector } from '@store/selectors/UserProfileSelector';
import { useSelector, useDispatch } from 'react-redux';
import { deleteCaseInUseStatus } from '@store/actions/CaseDetailsActionCreators';
import { BlockReleaseApiResponse } from '@data/blockReleases/types/BlockRelease.types';

interface UseSetBlockAccessorReturn {
	blockAccessor: SurgeryRequestAccessor[];
	setBlockReleaseRequestAccessor: () => void;
}

export const useSetBlockAccessor = (
	selectedRelease: BlockReleaseApiResponse,
): UseSetBlockAccessorReturn => {
	const { setter } = useSetAccessor(
		selectedRelease?.id,
		UserAccessEnum.OFFICE_MANAGE_RELEASE_NUDGE,
	);
	const { openModal } = useModalContext();
	const [blockAccessor, setBlockAccessor] = React.useState<BlockReleaseRequestAccessor[]>([]);
	const { userId } = useSelector(UserProfileDetailsSelector);
	const dispatch = useDispatch();

	const activeCardIdRef = React.useRef<string | null>(null);

	const removeBlockAccessor = React.useCallback(
		(cardId: string, attemptSetAccessor: () => void) => {
			dispatch(deleteCaseInUseStatus(cardId, userId, false, attemptSetAccessor));
		},
		[dispatch, userId],
	);

	const attemptSetAccessor = React.useCallback(() => {
		const onFailure = (error: AxiosError<BlockReleaseRequestAccessor[]>) => {
			if (error.response?.status === 409) {
				setBlockAccessor(error.response?.data);
				openModal(BLOCK_RELEASE_IN_USE_MODAL.id);
			}
		};

		const onSuccess = (response: AxiosResponse<BlockReleaseRequestAccessor[]>) => {
			setBlockAccessor(response?.data);
			activeCardIdRef.current = selectedRelease?.id;
		};

		setter(true, onSuccess, onFailure);
	}, [openModal, setter, selectedRelease]);

	const setBlockReleaseRequestAccessor = React.useCallback(() => {
		const isUserAccessorForCurrent =
			blockAccessor.length > 0 && blockAccessor[0].userId === userId;

		if (
			activeCardIdRef.current &&
			activeCardIdRef.current !== selectedRelease.id &&
			isUserAccessorForCurrent
		) {
			removeBlockAccessor(activeCardIdRef.current, attemptSetAccessor);
		} else {
			attemptSetAccessor();
		}
	}, [attemptSetAccessor, blockAccessor, removeBlockAccessor, selectedRelease?.id, userId]);

	React.useEffect(() => {
		const unloadHandler = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			if (activeCardIdRef.current) {
				removeBlockAccessor(activeCardIdRef.current, () => {});
			}
		};

		window.addEventListener('beforeunload', unloadHandler);

		return () => {
			window.removeEventListener('beforeunload', unloadHandler);
			// Handle cleanup for in-app navigation
			if (activeCardIdRef.current) {
				removeBlockAccessor(activeCardIdRef.current, () => {});
			}
		};
	}, [removeBlockAccessor]);

	return { blockAccessor, setBlockReleaseRequestAccessor };
};
